'use client';

import { useEffect } from 'react';

import type { Session } from '@supabase/supabase-js';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';

import {
  type HeaderMembership,
  toHeaderTeamAccount,
  toHeaderUserRole,
} from '~/lib/auth/header-staff-access';

const SESSION_QUERY_KEY = ['session'];
const STAFF_ACCESS_QUERY_KEY = 'header-staff-access';

/**
 * The browser session shared by every tenant/marketing header and nav. The auth
 * listener writes refreshed and signed-out sessions straight into the cache, so
 * a session whose refresh token was rejected stops rendering as signed in. A
 * change of signed-in user refetches staff access, which may have cached a
 * signed-out result under the same user id.
 */
export function useSession() {
  const client = useSupabase();
  const queryClient = useQueryClient();

  useEffect(() => {
    const { data: listener } = client.auth.onAuthStateChange(
      (_event, session) => {
        const previous = queryClient.getQueryData<Session | null>(
          SESSION_QUERY_KEY,
        );

        queryClient.setQueryData(SESSION_QUERY_KEY, session);

        if (previous?.user.id !== session?.user.id) {
          void queryClient.invalidateQueries({
            queryKey: [STAFF_ACCESS_QUERY_KEY],
          });
        }
      },
    );

    return () => listener.subscription.unsubscribe();
  }, [client, queryClient]);

  return useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: async () => {
      const { data } = await client.auth.getSession();

      return data.session;
    },
  });
}

/**
 * One memberships read shared by `useUserRole` and `useTeamAccount`.
 *
 * `getSession()` refreshes an expired access token first. When that refresh
 * fails the client would fall back to the publishable key, and PostgREST rejects
 * `accounts_memberships` for anon with a 401 — so a missing session resolves to
 * `null` (signed out) without sending the request.
 */
function useStaffAccess<T>(
  userId: string | undefined,
  select: (memberships: HeaderMembership[] | null) => T,
) {
  const client = useSupabase();

  return useQuery({
    queryKey: [STAFF_ACCESS_QUERY_KEY, userId],
    queryFn: async (): Promise<HeaderMembership[] | null> => {
      const { data: auth } = await client.auth.getSession();
      const sessionUserId = auth.session?.user.id;

      if (!sessionUserId || sessionUserId !== userId) return null;

      const { data, error } = await client
        .from('accounts_memberships')
        .select(
          'account_role, accounts!accounts_memberships_account_id_fkey(id, slug, name, is_personal_account)',
        )
        .eq('user_id', sessionUserId);

      return error ? [] : data;
    },
    select,
    enabled: !!userId,
  });
}

export function useUserRole(userId?: string) {
  return useStaffAccess(userId, toHeaderUserRole);
}

export function useTeamAccount(userId?: string) {
  return useStaffAccess(userId, toHeaderTeamAccount);
}
